/**
 * GameLogger - Central orchestrator for the logging system.
 * Decomposed from a monolithic structure for better maintainability.
 */

import { LogLevel, LogMode, LogEntry, LogListener, LOG_STYLES } from './logger/LogTypes';
import { LogSanitizer } from './logger/LogSanitizer';
import { LogFilters } from './logger/LogFilters';
import {
  ConsoleTransport,
  RemoteTransport,
  type RemoteLogContext,
  type RemoteTransportOptions,
  Transport,
} from './logger/LogTransports';
import { bootHref } from '../config/boot/bootUrl';

class GameLogger {
  private logs: LogEntry[] = [];
  private maxLogs: number = 10000;
  private isLogging: boolean = false;
  private sessionId: string;
  private currentMode: LogMode = LogMode.MINIMAL;
  private filters: LogFilters = new LogFilters();
  private listeners: Set<LogListener> = new Set();
  private transports: Transport[] = [];
  private remoteTransport: RemoteTransport | null = null;
  private nativeConsole: Console;
  private throttlers: Map<string, number> = new Map();

  constructor() {
    this.sessionId = this.generateSessionId();
    this.nativeConsole = this.captureNativeConsole();
    this.transports.push(new ConsoleTransport(this.nativeConsole));
    this.initializeLogger();
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private captureNativeConsole(): Console {
    return {
      ...console,
      log: console.log.bind(console),
      info: console.info.bind(console),
      warn: console.warn.bind(console),
      error: console.error.bind(console),
      debug: console.debug.bind(console),
      group: console.group.bind(console),
      groupEnd: console.groupEnd.bind(console)
    } as Console;
  }

  private initializeLogger(): void {
    // Default to BALANCED; caller can call setLogMode(LogMode.VERBOSE)
    // after construction if verbose logging is desired.
    this.setLogMode(LogMode.BALANCED);
    this.startLogging();

    this.info('boot', '🚀 GameLogger initialized', {
      sessionId: this.sessionId,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Node.js',
      url: typeof window !== 'undefined' ? bootHref() : 'localhost',
      timestamp: new Date().toISOString()
    });

    this.interceptConsole();
  }

  private interceptConsole(): void {
    console.log = (...args) => {
      const message = args.map(arg => LogSanitizer.safeStringify(arg)).join(' ');
      this.addLog(this.createLogEntry(LogLevel.INFO, 'CONSOLE', message, args));
    };

    console.info = (...args) => {
      this.addLog(this.createLogEntry(LogLevel.INFO, 'CONSOLE', args.map(String).join(' '), args));
    };

    console.warn = (...args) => {
      this.addLog(this.createLogEntry(LogLevel.WARN, 'CONSOLE', args.map(String).join(' '), args));
    };

    console.error = (...args) => {
      const message = args.map(arg => LogSanitizer.safeStringify(arg)).join(' ');
      this.addLog(this.createLogEntry(LogLevel.ERROR, 'CONSOLE', message, args));
      this.nativeConsole.error.apply(console, args);
    };

    console.debug = (...args) => {
      this.addLog(this.createLogEntry(LogLevel.DEBUG, 'CONSOLE', args.map(String).join(' '), args));
    };
  }

  private createLogEntry(level: LogLevel, category: string, message: string, data?: unknown): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      category,
      message,
      data,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Node.js',
      url: typeof window !== 'undefined' ? bootHref() : 'localhost'
    };
  }

  public setRemoteLogging(url: string, options: RemoteTransportOptions = {}): void {
    if (this.remoteTransport) {
      this.remoteTransport.stop();
      this.transports = this.transports.filter(t => t !== this.remoteTransport);
    }
    this.remoteTransport = new RemoteTransport(url, this.sessionId, this.nativeConsole, options);
    if (this.sessionContextProvider) {
      this.remoteTransport.setContextProvider(this.sessionContextProvider);
    }
    this.transports.push(this.remoteTransport);
    this.info('SYSTEM', `Remote logging enabled: ${url}`);
  }

  // G.3.1 (2026-05-14) — rich session-context provider. Called by RemoteTransport
  // at flush time; provider returns the latest known Colyseus sid + character
  // + user info. Server stamps these onto every log entry so MCP tools can
  // correlate without a separate fetch.
  private sessionContextProvider: (() => RemoteLogContext) | null = null;

  public setSessionContextProvider(provider: (() => RemoteLogContext) | null): void {
    this.sessionContextProvider = provider;
    if (this.remoteTransport) {
      this.remoteTransport.setContextProvider(provider);
    }
  }

  private addLog(entry: LogEntry): void {
    if (!this.isLogging) return;

    // Critical categories bypass rate limiting — these diagnose silent failures
    // at WARN/ERROR levels (level >= WARN bypasses anyway). DEBUG/INFO at these
    // categories still respect rate-limit + dedup so hot-path emitters
    // (e.g. physics-tick logs at 60Hz × 4 lines/frame = 240 alloc/sec) don't
    // generate GC pressure. H.1 (2026-05-13): removed 'physics' from this
    // list — it was firing PHYSICS_DELTA/JUMP_EXEC/GROUND_CHANGE/etc. every
    // frame, allocating LogEntry objects at 240+/sec. H.1.5 (2026-05-13):
    // removed 'network' for the same reason — NetworkManager.send fires
    // `gameLogger.debug('network', '📤 Sending <type>')` per outbound message
    // at 7-15/sec under input spam, same alloc-churn pattern. Errors at
    // level WARN/ERROR ALWAYS bypass (see filter logic below —
    // `entry.level < LogLevel.WARN`) so network catastrophes still surface.
    const ALWAYS_SHOW = ['cast', 'combat', 'registry', 'abilities'];
    const isCritical = ALWAYS_SHOW.includes(entry.category);

    // Filter Logic
    if (this.currentMode === LogMode.BALANCED && entry.level < LogLevel.WARN && !isCritical) {
      const limitResult = this.filters.isRateLimited(entry.category, entry.message);
      if (limitResult.limited) {
        this.storeInternally(entry);
        return;
      }
      if (limitResult.suppressed && limitResult.suppressed > 0) {
        this.nativeConsole.debug(
          `%c[${new Date().toISOString()}] [DEBUG] [RATE_LIMIT] Suppressed ${limitResult.suppressed} duplicate logs for "${entry.category}: ${entry.message.substring(0, 30)}..."`,
          LOG_STYLES.DEBUG
        );
      }
    }

    const dupCount = this.filters.checkDuplicate(entry.category, entry.message);
    if (dupCount > 1 && this.currentMode === LogMode.BALANCED && entry.level < LogLevel.WARN && !isCritical) {
      this.storeInternally(entry);
      return;
    }

    this.storeInternally(entry);

    // Notify listeners
    this.listeners.forEach(listener => {
      try { listener(entry); } catch (e) { this.nativeConsole.error('Error in GameLogger listener:', e); }
    });

    // Send to Transports
    const shouldPrintToConsole = this.filters.isCategoryEnabled(entry.category);
    this.transports.forEach(transport => {
      if (transport instanceof ConsoleTransport) {
        if (shouldPrintToConsole && entry.category !== 'CONSOLE') {
          transport.send(entry);
        }
      } else {
        transport.send(entry);
      }
    });
  }

  private storeInternally(entry: LogEntry): void {
    this.logs.push(entry);
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }
  }

  // Public API
  public trace(category: string, message: string, data?: unknown): void { this.addLog(this.createLogEntry(LogLevel.TRACE, category, message, data)); }
  public debug(category: string, message: string, data?: unknown): void { this.addLog(this.createLogEntry(LogLevel.DEBUG, category, message, data)); }
  public info(category: string, message: string, data?: unknown): void { this.addLog(this.createLogEntry(LogLevel.INFO, category, message, data)); }
  public success(category: string, message: string, data?: unknown): void { this.addLog(this.createLogEntry(LogLevel.SUCCESS, category, message, data)); }
  public warn(category: string, message: string, data?: unknown): void { this.addLog(this.createLogEntry(LogLevel.WARN, category, message, data)); }
  public error(category: string, message: string, data?: unknown): void { this.addLog(this.createLogEntry(LogLevel.ERROR, category, message, data)); }

  public setLogMode(mode: LogMode): void {
    this.currentMode = mode;
    this.isLogging = mode !== LogMode.SILENT;
    this.filters.applyMode(mode);
    this.nativeConsole.info(`%c📊 Log Mode set to: ${LogMode[mode]}`, LOG_STYLES.MODE);
  }

  public cycleLogMode(): LogMode {
    let next = this.currentMode + 1;
    if (next > LogMode.VERBOSE) next = LogMode.SILENT;
    this.setLogMode(next);
    return next;
  }

  public startLogging(): void { this.isLogging = true; }
  public stopLogging(): void { this.isLogging = false; if (this.remoteTransport) this.remoteTransport.stop(); }
  public clearLogs(): void { this.logs = []; }
  public getLogs(): LogEntry[] { return [...this.logs]; }
  public getSessionId(): string { return this.sessionId; }
  public isLoggingActive(): boolean { return this.isLogging; }
  
  public throttle(key: string, cooldownMs: number, logFn: () => void): void {
    const now = Date.now();
    const last = this.throttlers.get(key) || 0;
    if (now - last > cooldownMs) {
      this.throttlers.set(key, now);
      logFn();
    }
  }

  public subscribe(listener: LogListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  public setCategoryFlag(category: string, enabled: boolean): void { this.filters.setCategoryFlag(category, enabled); }
  public getCategoryFlags(): Record<string, boolean> { return this.filters.getCategoryFlags(); }
  public enableCategory(category: string): void { this.filters.setCategoryFlag(category, true); }
  public disableCategory(category: string): void { this.filters.setCategoryFlag(category, false); }

  // Specialized Logging
  public logInputEvent(element: string, eventType: string, event: any): void {
    this.debug('INPUT', `${element} ${eventType}`, {
      element, eventType, key: event.key, code: event.code, keyCode: event.keyCode,
      ctrlKey: event.ctrlKey, shiftKey: event.shiftKey, altKey: event.altKey, metaKey: event.metaKey
    });
  }

  public logCharacterInput(element: string, character: string, currentValue: string): void {
    this.debug('CHAR_INPUT', `Character "${character}" entered in ${element}`, { element, character, currentValue });
  }

  public logClick(element: string, details?: any): void { this.info('CLICK', `Clicked on ${element}`, { element, ...details }); }
  public logNavigation(from: string, to: string): void { this.info('NAVIGATION', `Navigation from ${from} to ${to}`, { from, to, timestamp: Date.now() }); }

  public getLogStats(): Record<string, any> {
    const stats: Record<string, any> = { total: this.logs.length, byLevel: {}, byCategory: {}, sessionId: this.sessionId, isActive: this.isLogging };
    this.logs.forEach(log => {
      const levelName = LogLevel[log.level];
      stats.byLevel[levelName] = (stats.byLevel[levelName] || 0) + 1;
      stats.byCategory[log.category] = (stats.byCategory[log.category] || 0) + 1;
    });
    return stats;
  }

  public getSystemHealth(): Record<string, any> {
    const health: Record<string, any> = {};
    const now = Date.now();
    this.logs.forEach(log => {
      const system = log.category;
      if (!health[system]) health[system] = { isHealthy: true, errorCount: 0, warningCount: 0, lastEventTime: 0 };
      const logTime = new Date(log.timestamp).getTime();
      health[system].lastEventTime = Math.max(health[system].lastEventTime, logTime);
      if (log.level === LogLevel.ERROR) { health[system].errorCount++; health[system].isHealthy = false; }
      else if (log.level === LogLevel.WARN) health[system].warningCount++;
    });
    Object.keys(health).forEach(system => { health[system].lastEvent = now - health[system].lastEventTime; });
    return health;
  }

  // Export methods
  public exportToText(): string {
    return this.logs.map(log => `[${log.timestamp}] [${LogLevel[log.level]}] [${log.category}] ${log.message}`).join('\n');
  }

  public downloadLog(format: 'text' | 'json' = 'text'): void {
    const content = format === 'json' ? JSON.stringify(this.logs, null, 2) : this.exportToText();
    const blob = new Blob([content], { type: format === 'json' ? 'application/json' : 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `helix-log-${Date.now()}.${format === 'json' ? 'json' : 'txt'}`;
    link.click();
    URL.revokeObjectURL(url);
  }
}

export const gameLogger = new GameLogger();

// Export everything for backward compatibility
export { LogLevel, LogMode, LOG_STYLES };
export type { LogEntry, LogListener };
export const logDebug = (category: string, message: string, data?: any) => gameLogger.debug(category, message, data);
export const logInfo = (category: string, message: string, data?: any) => gameLogger.info(category, message, data);
export const logWarn = (category: string, message: string, data?: any) => gameLogger.warn(category, message, data);
export const logError = (category: string, message: string, data?: any) => gameLogger.error(category, message, data);
export const logInput = (element: string, eventType: string, event: any) => gameLogger.logInputEvent(element, eventType, event);
export const logCharacter = (element: string, character: string, currentValue: string) => gameLogger.logCharacterInput(element, character, currentValue);
export const logClick = (element: string, details?: any) => gameLogger.logClick(element, details);

// Re-export from specialized loggers for backward compatibility
export { createRateLimitedLogger, throttledLog, RateLimitedLogger } from './logger/SpecializedLoggers';
